import React, { useState } from 'react';
import { AuthenticatedTemplate } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { ExpandLess, ExpandMore, AccountBalance as BudgetIcon, Rule as RulesIcon, Timeline as ProjectionsIcon } from '@mui/icons-material';
import { Collapse, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { useAppSelector } from '../../../../../app/hooks';
import { useGetMyProfileQuery } from '../../../../../services/ProfileService';
import { hasLaborManagementAccess, hasLaborManagementAdminAccess } from '../../../../profile/utils/profileUtils';

// Types
interface LaborMenuItemConfig {
    id: string;
    title: string;
    icon: React.ComponentType<{ sx?: any }>;
    route: string;
    adminOnly?: boolean;
}

// Constants
const MENU_STYLES = {
    navList: {
        border: 'black solid 1px'
    },
    nestedListItem: {
        marginTop: '0px',
        paddingLeft: '48px'
    },
    icon: {
        color: 'white'
    }
} as const;

const LABOR_MENU_ITEMS: LaborMenuItemConfig[] = [
    {
        id: 'budget',
        title: 'Budget',
        icon: BudgetIcon,
        route: '/labor-management/budget',
    },
    {
        id: 'projections',
        title: 'Projections',
        icon: ProjectionsIcon,
        route: '/labor-management/projections',
    },
    {
        id: 'rules',
        title: 'Rules',
        icon: RulesIcon,
        route: '/labor-management/rules',
        adminOnly: true,
    },
];

// Sub-components
interface LaborMenuItemProps {
    item: LaborMenuItemConfig;
    onClick: () => void;
    disabled?: boolean;
}

const LaborMenuItem: React.FC<LaborMenuItemProps> = ({ item, onClick, disabled = false }) => {
    const { title, icon: Icon } = item;

    return (
        <ListItemButton
            sx={{
                ...MENU_STYLES.nestedListItem,
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? 'not-allowed' : 'pointer'
            }}
            onClick={disabled ? undefined : onClick}
            disabled={disabled}
        >
            <ListItemIcon>
                <Icon sx={MENU_STYLES.icon} />
            </ListItemIcon>
            <ListItemText primary={title} />
        </ListItemButton>
    );
};

// Main component
export const LaborMenu: React.FC = () => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const selectedCenter = useAppSelector(state => state.center.selectedCenter);
    const { data: profile } = useGetMyProfileQuery();
    const company = profile?.organization || '';

    const handleItemClick = (item: LaborMenuItemConfig): void => {
        if (!company || !selectedCenter) {
            console.warn('Cannot navigate: missing required parameters', {
                company: company || 'missing',
                selectedCenter: selectedCenter || 'missing'
            });
            return;
        }

        const params = new URLSearchParams();
        params.append('company', company);
        params.append('center', selectedCenter);

        const routeWithParams = `${item.route}?${params.toString()}`;
        navigate(routeWithParams);
    };

    const toggleMenu = (): void => setOpen(!open);

    if (!hasLaborManagementAccess(profile)) {
        return null;
    }

    const isAdmin = hasLaborManagementAdminAccess(profile);
    const visibleItems = LABOR_MENU_ITEMS.filter(item => !item.adminOnly || isAdmin);

    return (
        <AuthenticatedTemplate>
            <List component="nav" sx={MENU_STYLES.navList}>
                <ListItemButton onClick={toggleMenu}>
                    <ListItemText primary="Labor Management" />
                    {open ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={open} timeout="auto" unmountOnExit>
                    {visibleItems.map((item) => {
                        const isDisabled = !company;
                        return (
                            <LaborMenuItem
                                key={item.id}
                                item={item}
                                onClick={() => handleItemClick(item)}
                                disabled={isDisabled}
                            />
                        );
                    })}
                </Collapse>
            </List>
        </AuthenticatedTemplate>
    );
};

export default LaborMenu;
